import {
  Box, Container, Wrap,
  HStack,
  Heading,
  Grid,
  SimpleGrid,
  VStack,
} from '@chakra-ui/react';
import { useRef } from 'react';

import MainLayout from '@src/components/layouts/main';
import { selectedDatabaseIdState, useIsUserInRole } from '@transport-insights/uikit';
import PrintablePdf from '@src/modules/performance/components/PrintablePdf';
import { useRecoilValue } from 'recoil';
import EmptyState from '@src/components/shared/EmptyState';
import ServicePerformanceReportHeader from './components/report-headers/ServicePerformanceReportHeader';
import ServicePerformanceAnnualTargets from './components/report-sections/ServicePerformanceAnnualTargets';
import TransportProgrammeDeliveryReportHeader from './components/report-headers/TransportProgrammeDeliveryReportHeader';
import TransportProgrammeDeliveryActivityClassExpenditure from './components/report-sections/TransportProgrammeDeliveryActivityClassExpenditure';
import TransportProgrammeDeliveryFunding from './components/report-sections/TransportProgrammeDeliveryFunding';
import TransportProgrammeDeliveryValuation from './components/report-sections/TransportProgrammeDeliveryValuation';
import RoadMaintenanceDeliveryReportHeader from './components/report-headers/RoadMaintenanceDeliveryReportHeader';
import RoadMaintenanceDeliveryCosts from './components/report-sections/RoadMaintenanceDeliveryCosts';
import RoadMaintenanceDeliveryActivitiesRoads from './components/report-sections/RoadMaintenanceDeliveryActivitiesRoads';
import RoadMaintenanceDeliveryActivitiesDrainage from './components/report-sections/RoadMaintenanceDeliveryActivitiesDrainage';
import RoadMaintenanceDeliveryOtherWorks from './components/report-sections/RoadMaintenanceDeliveryOtherWorks';
import RoadSafetyReportHeader from './components/report-headers/RoadSafetyReportHeader';
import RoadSafetyNetwork from './components/report-sections/RoadSafetyNetwork';
import RoadSafetyByMode from './components/report-sections/RoadSafetyByMode';
import RoadSafetyCrashDistributionStreetCategories from './components/report-sections/RoadSafetyCrashDistributionStreetCategories';
import RoadConditionReportHeader from './components/report-headers/RoadConditionReportHeader';
import RoadConditionSealedRoads from './components/report-sections/RoadConditionSealedRoads';
import ActivityManagementReportHeader from './components/report-headers/ActivityManagementReportHeader';
import ActivityManagementPlanning from './components/report-sections/ActivityManagementPlanning';
import ActivityManagementProcurement from './components/report-sections/ActivityManagementProcurement';
import CoInvestorAssuranceReportHeader from './components/report-headers/CoInvestorAssuranceReportHeader';
import CoInvestorAssuranceAudit from './components/report-sections/CoInvestorAssuranceAudit';
import PublicTransportReportHeader from './components/report-headers/PublicTransportReportHeader';
import PublicTransportUse from './components/report-sections/PublicTransportUse';
import PublicTransportFleetSize from './components/report-sections/PublicTransportFleetSize';
import PublicTransportCommunityServiced from './components/report-sections/PublicTransportCommunityServiced';
import PublicTransportCommunities from './components/report-sections/PublicTransportCommunities';
import PublicTransportExpenditure from './components/report-sections/PublicTransportExpenditure';
import PublicTransportTotalMobilityUse from './components/report-sections/PublicTransportTotalMobilityUse';
import PublicTransportTotalMobilityCommunities from './components/report-sections/PublicTransportTotalMobilityCommunities';
import NetworkAvailabilityReportHeader from './components/report-headers/NetworkAvailabilityReportHeader';
import NetworkAvailabilityResilienceDistributionsStreetCategory from './components/report-sections/NetworkAvailabilityResilienceDistributionsStreetCategory';
import NetworkPhysicalCharacteristicsReportHeader from './components/report-headers/NetworkPhysicalCharacteristicsReportHeader';
import NetworkPhysicalCharacteristicsRoads from './components/report-sections/NetworkPhysicalCharacteristicsRoads';
import NetworkPhysicalCharacteristicsCycleways from './components/report-sections/NetworkPhysicalCharacteristicsCycleways';
import NetworkPhysicalCharacteristicsBridges from './components/report-sections/NetworkPhysicalCharacteristicsBridges';
import RoadNetworkUseReportHeader from './components/report-headers/RoadNetworkUseReportHeader';
import TerritorialActivityReportHeader from './components/report-headers/TerritorialActivityReportHeader';
import TerritorialActivityPopulation from './components/report-sections/TerritorialActivityPopulation';
import TerritorialActivityEconomic from './components/report-sections/TerritorialActivityEconomic';
import StatsWidget from '../../components/dashboard-widgets/StatsWidget';
import { useAllowedReportsList } from '../../hooks/useReportsList';
import widgetsMap from '../../shared/availableDashboardWidgets';
import RoadConditionAmenitySealedRoads from './components/report-sections/RoadConditionAmenitySealedRoads';
import RoadConditionRoadRoughness from './components/report-sections/RoadConditionRoadRoughness';
import RoadMaintenanceDeliveryQuarterlyCosts from './components/report-sections/RoadMaintenanceDeliveryQuarterlyCosts';
import E3ReportHeader from './components/report-headers/E3ReportHeader';
import E3PeerGroup from './components/report-sections/E3PeerGroup';
import E3Trends from './components/report-sections/E3Trends';
import E3Quality from './components/report-sections/E3Quality';
import E3Cost from './components/report-sections/E3Cost';
import E3Quantity from './components/report-sections/E3Quantity';
import TransportProgrammeDeliveryRoadSafety from './components/report-sections/TransportProgrammeDeliveryRoadSafety';
import TransportProgrammeDeliveryWalkingCycling from './components/report-sections/TransportProgrammeDeliveryWalkingCycling';
import TransportProgrammeDeliveryManagement from './components/report-sections/TransportProgrammeDeliveryManagement';
import DataQualityReportHeader from './components/report-headers/DataQualityReportHeader';
import DataQualityExpectedStandard from './components/report-sections/DataQualityExpectedStandard';
import DataQualityScore from './components/report-sections/DataQualityScore';
import RoadNetworkUseRoadUse from './components/report-sections/RoadNetworkUseRoadUse';
import RoadNetworkUseRestrictedBridges from './components/report-sections/RoadNetworkUseRestrictedBridges';
import RoadMaintenanceDeliveryPavementResurfacing from './components/report-sections/RoadMaintenanceDeliveryPavementResurfacing';
import RoadSafetyDsi from './components/report-sections/RoadSafetyDsi';
import RoadSafetyCrashDistributionFreight from './components/report-sections/RoadSafetyCrashDistributionFreight';
import RoadSafetyCrashDistributionGeneralTraffic from './components/report-sections/RoadSafetyCrashDistributionGeneralTraffic';
import TransportProgrammeDeliveryCostEfficiencyTotalExpenditure from './components/report-sections/TransportProgrammeDeliveryCostEfficiencyTotalExpenditure';
import TransportProgrammeDeliveryCostEfficiencyMaintenance from './components/report-sections/TransportProgrammeDeliveryCostEfficiencyMaintenance';
import RoadNetworkUseJourneyDistributionGeneralTraffic from './components/report-sections/RoadNetworkUseJourneyDistributionGeneralTraffic';
import RoadNetworkUseJourneyDistributionFreight from './components/report-sections/RoadNetworkUseJourneyDistributionFreight';
import RoadNetworkUseJourneyDistributionOnrc from './components/report-sections/RoadNetworkUseJourneyDistributionOnrc';
import RoadNetworkUseJourneyDistributionStreetCategory from './components/report-sections/RoadNetworkUseJourneyDistributionStreetCategory';
import RoadSafetyCrashFactors from './components/report-sections/RoadSafetyCrashFactors';
import RoadSafetyCrashConditions from './components/report-sections/RoadSafetyCrashConditions';
import RoadSafetyRoadAssessmentRating from './components/report-sections/RoadSafetyRoadAssessmentRating';
import RoadNetworkUseHeavyVehicleRoadUse from './components/report-sections/RoadNetworkUseHeavyVehicleRoadUse';
import RoadConditionRutting from './components/report-sections/RoadConditionRutting';
import RoadConditionTexture from './components/report-sections/RoadConditionTexture';
import NetworkAvailabilityFreightAccessibilityDistributionOnrc from './components/report-sections/NetworkAvailabilityFreightAccessibilityDistributionOnrc';
import NetworkAvailabilityFreightAccessibilityDistributionStreetCategory from './components/report-sections/NetworkAvailabilityFreightAccessibilityDistributionStreetCategory';
import NetworkAvailabilityFreightAccessibilityDistributionFreight from './components/report-sections/NetworkAvailabilityFreightAccessibilityDistributionFreight';
import NetworkAvailabilityFreightAccessibilityDistributionGeneralTraffic from './components/report-sections/NetworkAvailabilityFreightAccessibilityDistributionGeneralTraffic';
import NetworkPhysicalCharacteristicsAverageLife from './components/report-sections/NetworkPhysicalCharacteristicsAverageLife';
import NetworkAvailabilityResilienceDistributionsOnrc from './components/report-sections/NetworkAvailabilityResilienceDistributionsOnrc';
import NetworkAvailabilityResilienceDistributionsFreight from './components/report-sections/NetworkAvailabilityResilienceDistributionsFreight';
import NetworkAvailabilityResilienceDistributionsGeneralTraffic from './components/report-sections/NetworkAvailabilityResilienceDistributionsGeneralTraffic';

function FullReport() {
  const printableRef = useRef();
  const hasDeveloperRole = useIsUserInRole('Developer');
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const allowedReportsList = useAllowedReportsList(rcaId);
  // Not available for State Highways or Doc Roads
  const isAllowedRca = rcaId !== 81 && rcaId !== 88 && rcaId < 1000;

  const defaultReportsList = Object.keys(allowedReportsList).filter((key) => {
    const report = allowedReportsList[key];

    return report.enabled;
  });

  return (
    <MainLayout backgroundColor="gray.50">
      <Container maxW="full" px={4} pb={4} flexGrow="1">
        <HStack justify="space-between">
          <Heading as="h1">Full Performance Report</Heading>
          {hasDeveloperRole && isAllowedRca && (
            <PrintablePdf
              fileName="Performance Report"
              pageTitle="Performance Report"
              printableRef={printableRef}
              printableStyles={null}
            />
          )}
        </HStack>
        <Container
          maxW="full"
          flexDirection="column"
          display="flex"
          height="100%"
          flexGrow={1}
          mt={4}
          p={0}
          position="relative"
          sx={{
            '@media print': {
              '@page': {
                size: 'landscape',
                margin: '0',
              },
            },
          }}
        >
          {hasDeveloperRole && isAllowedRca && (
          <Box ref={printableRef}>
            <Box mb={2}>
              <SimpleGrid
                spacing={2}
                columns={{
                  base: 1, sm: 2, md: 3, lg: 4, xl: 5, '2xl': 5, '3xl': 6, '4xl': 8,
                }}
                className="dashboard-grid"
                sx={{
                  '@media print': {
                    gridTemplateColumns: 'repeat(6, minmax(64mm,1fr))!important',
                    gap: '2mm',
                  },
                }}
              >
                {defaultReportsList.map((key) => {
                  const WidgetComponent = widgetsMap[key];
                  return (
                    WidgetComponent ? (
                      <WidgetComponent key={key} />
                    ) : null
                  );
                })}
              </SimpleGrid>
              <StatsWidget />
            </Box>
            {/* E3 Report */}
            <Box className="page-break-after" mb={2}>
              <E3ReportHeader
                position="relative"
                top="0"
              />
              <Grid
                templateColumns={{ base: '1fr', '2xl': '1fr 2fr' }}
                gap={2}
                alignItems="start"
                className="e3-grid"
              >
                <E3PeerGroup />
                <VStack spacing={2} align="stretch">
                  <E3Trends />
                  <Wrap
                    spacing={2}
                    flexGrow={1}
                  >
                    <E3Quality />
                    <E3Cost />
                    <E3Quantity />
                  </Wrap>
                </VStack>
              </Grid>
            </Box>
            {/* Service Performance Report */}
            <Box className="page-break-after" mb={2}>
              <ServicePerformanceReportHeader position="relative" top="0" />
              <Wrap spacing={2} className="report-sections-wrapper" mb={2}>
                <ServicePerformanceAnnualTargets />
              </Wrap>
            </Box>
            {/* Transport Programme Delivery Report */}
            <Box className="page-break-after" mb={2}>
              <TransportProgrammeDeliveryReportHeader
                position="relative"
                top="0"
              />
              <Wrap spacing={2} className="report-sections-wrapper">
                <TransportProgrammeDeliveryActivityClassExpenditure />
                <TransportProgrammeDeliveryFunding />
                <TransportProgrammeDeliveryValuation />
                <TransportProgrammeDeliveryRoadSafety />
                <TransportProgrammeDeliveryWalkingCycling />
                <TransportProgrammeDeliveryManagement />
                <TransportProgrammeDeliveryCostEfficiencyTotalExpenditure />
                <TransportProgrammeDeliveryCostEfficiencyMaintenance />
              </Wrap>
            </Box>
            {/* Road Maintenance Delivery Report */}
            <Box className="page-break-after" mb={2}>
              <RoadMaintenanceDeliveryReportHeader
                position="relative"
                top="0"
              />
              <Wrap spacing={2} className="report-sections-wrapper">
                <RoadMaintenanceDeliveryQuarterlyCosts />
                <RoadMaintenanceDeliveryCosts />
                <RoadMaintenanceDeliveryActivitiesRoads />
                <RoadMaintenanceDeliveryPavementResurfacing />
                <RoadMaintenanceDeliveryActivitiesDrainage />
                <RoadMaintenanceDeliveryOtherWorks />
              </Wrap>
            </Box>
            {/* Road Safety Report */}
            <Box className="page-break-after" mb={2}>
              <RoadSafetyReportHeader
                position="relative"
                top="0"
              />
              <Wrap spacing={2} className="report-sections-wrapper">
                <RoadSafetyDsi />
                <RoadSafetyNetwork />
                <RoadSafetyCrashFactors />
                <RoadSafetyByMode />
                <RoadSafetyCrashConditions />
                <RoadSafetyRoadAssessmentRating />
                <RoadSafetyCrashDistributionGeneralTraffic />
                <RoadSafetyCrashDistributionFreight />
                <RoadSafetyCrashDistributionStreetCategories />
              </Wrap>
            </Box>
            {/* Road Condition Report */}
            <Box className="page-break-after" mb={2}>
              <RoadConditionReportHeader
                position="relative"
                top="0"
              />
              <Wrap spacing={2} className="report-sections-wrapper" mb={2}>
                <RoadConditionAmenitySealedRoads />
                <RoadConditionRoadRoughness />
                <RoadConditionSealedRoads />
                <RoadConditionRutting />
                <RoadConditionTexture />
              </Wrap>
            </Box>
            <Box className="page-break-after" mb={2}>
              {/* Activity Management Report */}
              <ActivityManagementReportHeader
                position="relative"
                top="0"
              />
              <Wrap spacing={2} className="report-sections-wrapper" mb={2}>
                <ActivityManagementPlanning />
                <ActivityManagementProcurement />
              </Wrap>
              {/* Data Quality Report */}
              <DataQualityReportHeader
                position="relative"
                top="0"
              />
              <Wrap spacing={2} className="report-sections-wrapper" mb={2}>
                <DataQualityScore />
                <DataQualityExpectedStandard />
              </Wrap>
            </Box>
            {/* Co-Investor Assurance Report */}
            <Box className="page-break-after" mb={2}>
              <CoInvestorAssuranceReportHeader
                position="relative"
                top="0"
              />
              <CoInvestorAssuranceAudit />
            </Box>
            {/* Public Transport Report */}
            <Box className="page-break-after" mb={2}>
              <PublicTransportReportHeader
                position="relative"
                top="0"
              />
              <Wrap spacing={2} className="report-sections-wrapper">
                <PublicTransportUse />
                <PublicTransportFleetSize />
                <PublicTransportCommunityServiced />
                <PublicTransportCommunities />
                <PublicTransportExpenditure />
                <PublicTransportTotalMobilityUse />
                <PublicTransportTotalMobilityCommunities />
              </Wrap>
            </Box>
            {/* Network Availability Report */}
            <Box className="page-break-after" mb={2}>
              <NetworkAvailabilityReportHeader
                position="relative"
                top="0"
              />
              <Wrap spacing={2} className="report-sections-wrapper">
                <NetworkAvailabilityResilienceDistributionsOnrc />
                <NetworkAvailabilityResilienceDistributionsStreetCategory />
                <NetworkAvailabilityResilienceDistributionsFreight />
                <NetworkAvailabilityResilienceDistributionsGeneralTraffic />
                <NetworkAvailabilityFreightAccessibilityDistributionOnrc />
                <NetworkAvailabilityFreightAccessibilityDistributionStreetCategory />
                <NetworkAvailabilityFreightAccessibilityDistributionFreight />
                <NetworkAvailabilityFreightAccessibilityDistributionGeneralTraffic />
              </Wrap>
            </Box>

            <Box className="page-break-after" mb={2}>
              {/* Network Physical Characteristics Report */}
              <NetworkPhysicalCharacteristicsReportHeader
                position="relative"
                top="0"
              />
              <Wrap spacing={2} className="report-sections-wrapper" mb={2}>
                <NetworkPhysicalCharacteristicsRoads />
                <NetworkPhysicalCharacteristicsCycleways />
                <NetworkPhysicalCharacteristicsBridges />
                <NetworkPhysicalCharacteristicsAverageLife />
              </Wrap>
            </Box>
            {/* Road Network Use Report */}
            <Box className="page-break-after" mb={2}>
              <RoadNetworkUseReportHeader
                position="relative"
                top="0"
              />
              <Wrap spacing={2} className="report-sections-wrapper">
                <RoadNetworkUseRoadUse />
                <RoadNetworkUseHeavyVehicleRoadUse />
                <RoadNetworkUseRestrictedBridges />
                <RoadNetworkUseJourneyDistributionGeneralTraffic />
                <RoadNetworkUseJourneyDistributionFreight />
                <RoadNetworkUseJourneyDistributionStreetCategory />
                <RoadNetworkUseJourneyDistributionOnrc />
              </Wrap>
            </Box>
            {/* Territorial Activity Report */}
            <Box className="page-break-after" mb={2}>
              <TerritorialActivityReportHeader
                position="relative"
                top="0"
              />
              <Wrap spacing={2} className="report-sections-wrapper">
                <TerritorialActivityPopulation />
                <TerritorialActivityEconomic />
              </Wrap>
            </Box>
          </Box>
          )}
          {!hasDeveloperRole && (
            <EmptyState
              title="No Access"
              message="Sorry, you do not have access to this report."
            />
          )}
          {hasDeveloperRole && !isAllowedRca && (
            <EmptyState
              title="Report Unavailable"
              message="Sorry, this report is not available for the selected RCA."
            />
          )}
        </Container>
      </Container>
    </MainLayout>
  );
}

export default FullReport;
